/**
 * 预习卡（**家长端与孩子端共用的一份实现**）——
 * `/students/:id/dictation/:sessionId/preview` 与
 * `/student/dictation/:sessionId/preview` 都渲染它。
 *
 * ## 用户原话（这一步存在的全部理由）
 *
 * > "比如我选好这次要听写 10 个词……列出来放在**田字格**里面，上面标注有**拼音**，
 * > 最好能有**笔画顺序的动画演示**。这样孩子在听写之前，可以先预习看一下那个字
 * > 怎么写，预习之后再开始听写。"
 *
 * 2026-10-28 用户补充（孩子端要有同一个入口）：
 *
 * > "家长端布置完听写任务并点击听写，右上角有一个"先预习"的功能。
 * >  我觉得孩子那边也要加一个这个入口，在听写之前，他可以先预习，点进去预习。"
 *
 * 所以这一页是**听写之前**看的一页：这一轮要听写的词一个不少地列出来，每个字
 * 一格田字格、头顶标拼音，**点某个字就能看它一笔一笔怎么写**。看完回播放器点
 * 「开始播放」。
 *
 * ## 为什么抽成"面板"而不是复制一份孩子端页面
 *
 * 两边要显示的东西**完全一样**（词、拼音、田字格、笔顺、录音），差别只有三处：
 *   1. 外壳（`AppShell kind`：家长端 / 孩子端）；
 *   2. 链接（家长是 `/students/:id/...`，孩子是 `/student/...`）；
 *   3. 孩子端**没有**"看这次的全部题目"（那个页面是家长路由，孩子点进去会 403）。
 * 复制一份页面 = 三处之外还有 400 行潜在的漂移点。所以差别做成 props。
 *
 * ## 这一页刻意**不做**的事
 *
 *   · **不遮拼音、不做自测模式**：用户只说了"先看一下怎么写"。多一个模式就多一处
 *     要解释的东西，孩子的注意力也会被"点哪个按钮"分走；
 *   · **不念答案**：这里放的录音就是**听写时会放的那一段**（家长录音），
 *     除此之外不额外提示"会读什么"。所以家长录音可以听（等于提前听一遍），
 *     而**没有任何**"标准答案之外的提示"（例如组词、笔顺名称的语音）。
 *
 * ## 三态（与播放器同一口径，见 `ParentDictationSessionPage` 文件头）
 *
 * `questions === null` = **题目没拿到**（不是"这次没有题目"）；空数组才是真的没有。
 * 这一页在两种情况下给的界面完全不同 —— 混起来会让家长以为功能坏了。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AppShell } from '../layout/AppShell';
import { Badge, Card, CardHeader, EmptyState, Skeleton } from '../ui/Card';
import { Button } from '../ui/Button';
import { ErrorState } from '../ui/Bits';
import { useToast } from '../ui/Toast';
import { IconAlertTriangle, IconMic, IconVolume2 } from '../ui/Icons';
import { toErrorMessage } from '../../lib/api/error';
import { countLabel } from '../../lib/api/dictation';
import * as sessionApi from '../../lib/api/dictationSessions';
import { describeSessionOrigin } from '../../lib/dictationBuild';
import {
  buildPreviewWords,
  describePreviewMistake,
  summarizePreview,
  type PreviewWord,
} from '../../lib/dictationPreview';
import { TianZiGeChar } from './TianZiGe';
import { StrokeOrderCard } from './StrokeOrder';
import { strokesBundled } from '../../lib/hanziStrokes';

/** 当前打开笔顺面板的那个字（`null` = 都收起了）。 */
interface SelectedChar {
  questionId: string;
  position: number;
}

export interface DictationPreviewPanelProps {
  /** 外壳：家长端 / 孩子端（只影响 `AppShell` 的导航与标题样式）。 */
  kind: 'parent' | 'student';
  /** `students.id` */
  studentId: string;
  sessionId: string;
  /** 回到**这一次的**播放器（`/students/:id/dictation/:sid` 或 `/student/dictation/:sid`）。 */
  playerHref: string;
  /** 回听写页 / 任务列表（空题目时那个按钮也用它）。 */
  listHref: string;
  /**
   * "看这次的全部题目"的链接。**只有家长端传** ——
   * 那个页面在 `/students/:id/dictation/:sid/review`，是家长路由；
   * 孩子传了就等于渲染一个点进去 403 的按钮（本项目明确反对）。
   */
  reviewHref?: string;
  /** 顶部"这些就是等一下要听写的词"那句里，"回去点哪个按钮"。 */
  playerHint?: string;
}

export function DictationPreviewPanel({
  kind,
  studentId,
  sessionId,
  playerHref,
  listHref,
  reviewHref,
  playerHint = '回上一页点「开始播放」',
}: DictationPreviewPanelProps) {
  const toast = useToast();
  const pageTitle = '先预习一下';

  const sessionQuery = useQuery({
    queryKey: ['dictation-sessions', studentId, 'detail', sessionId] as const,
    queryFn: () => sessionApi.getSession(studentId, sessionId),
    enabled: studentId !== '' && sessionId !== '',
  });

  const session = sessionQuery.data ?? null;
  const questions = session?.questions ?? null;

  /** ⚠️ 必须记忆化：每次渲染造新数组会让下面的列表整体重建（也白白重算拼音分配）。 */
  const words = useMemo(() => buildPreviewWords(questions ?? []), [questions]);
  const summary = useMemo(() => summarizePreview(words), [words]);

  const [selected, setSelected] = useState<SelectedChar | null>(null);

  // ---------------------------------------------------------------- 家长录音
  /**
   * 预习时也可以听一遍**家长录的那一段**（听写时放的就是它）。
   *
   * 与"听写时不该听到答案"不冲突：这是**预习**环节，词面本来就明明白白写在
   * 田字格上，听一遍读音只是把"这个字怎么读"讲清楚。
   */
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioState, setAudioState] = useState<{
    questionId: string;
    status: 'loading' | 'playing';
  } | null>(null);

  const stopAudio = useCallback(() => {
    const element = audioRef.current;
    audioRef.current = null;
    if (element !== null) {
      element.pause();
      element.onended = null;
      element.onerror = null;
    }
    setAudioState(null);
  }, []);

  /** 离开这一页时掐掉正在放的声音（否则它会跟着一路播回播放器）。 */
  useEffect(() => stopAudio, [stopAudio]);

  const playRecording = useCallback(
    async (word: PreviewWord) => {
      if (word.wordId === null) return;
      stopAudio();
      setAudioState({ questionId: word.questionId, status: 'loading' });
      try {
        const audio = await sessionApi.getAudio(studentId, word.wordId);
        if (audio === null) {
          setAudioState(null);
          toast.showToast('这个词现在没有家长录音。', 'error');
          return;
        }
        const element = new Audio(audio.dataUrl);
        audioRef.current = element;
        element.onended = () => stopAudio();
        element.onerror = () => {
          stopAudio();
          toast.showToast('这段录音放不出来（文件可能坏了）。', 'error');
        };
        await element.play();
        setAudioState({ questionId: word.questionId, status: 'playing' });
      } catch (error) {
        setAudioState(null);
        toast.showToast(`没能放出这段录音：${toErrorMessage(error)}`, 'error');
      }
    },
    [studentId, stopAudio, toast],
  );

  // ---------------------------------------------------------------- 渲染
  if (sessionQuery.isLoading) {
    return (
      <AppShell kind={kind} title={pageTitle}>
        <div className="space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AppShell>
    );
  }

  if (sessionQuery.isError || session === null) {
    return (
      <AppShell kind={kind} title={pageTitle}>
        <ErrorState
          message={`没拿到这次听写：${toErrorMessage(sessionQuery.error)}`}
          onRetry={() => void sessionQuery.refetch()}
        />
      </AppShell>
    );
  }

  return (
    <AppShell kind={kind} title={pageTitle}>
      <div className="space-y-4">
        {/* ---------- 标题与汇总 ---------- */}
        <Card>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-lg font-bold text-slate-900">{session.title}</h2>
              <p className="mt-1 text-sm text-slate-500">
                {describeSessionOrigin(session.pickCriteria, session.tempo)}
              </p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-1.5">
              <Badge tone={questions === null ? 'warn' : 'brand'}>
                共 {countLabel(questions === null ? null : summary.wordCount)} 个词
              </Badge>
              {questions !== null && (
                <Badge tone="neutral">{countLabel(summary.charCount)} 个字</Badge>
              )}
            </div>
          </div>

          {questions !== null && questions.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-1.5">
              {summary.mistakeWordCount > 0 && (
                <Badge tone="warn">错词 {summary.mistakeWordCount} 个</Badge>
              )}
              {summary.audioWordCount > 0 && (
                <Badge tone="success">有家长录音 {summary.audioWordCount} 个</Badge>
              )}
              {summary.charsWithoutPinyin > 0 && (
                <Badge tone="warn">{summary.charsWithoutPinyin} 个字没标读音</Badge>
              )}
              {/*
                ⚠️ "没拿到"与"确实没有"分开说：把前者说成后者就是替后端撒谎。
              */}
              {summary.unknownMistakeWordCount > 0 && (
                <Badge tone="warn">错词情况未获取 {summary.unknownMistakeWordCount} 个</Badge>
              )}
              {summary.unknownAudioWordCount > 0 && (
                <Badge tone="warn">录音情况未获取 {summary.unknownAudioWordCount} 个</Badge>
              )}
            </div>
          )}
        </Card>

        {/* ---------- 题目拿不到 / 真的没有题目 ---------- */}
        {questions === null ? (
          <p className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            没拿到这次的题目（后端返回的字段名与预期不一致）—— 这不是"这次听写没有题目"。
            <Button
              size="sm"
              variant="secondary"
              className="ml-2"
              onClick={() => void sessionQuery.refetch()}
            >
              重新取一次
            </Button>
          </p>
        ) : questions.length === 0 ? (
          <EmptyState
            icon="📝"
            title="这次听写没有题目"
            description="正常情况下不会有这种会话（布置时一个词都没取到会直接报错）。请重新布置一次。"
            action={
              <Link to={listHref} className="mt-2">
                <Button size="sm">回听写页</Button>
              </Link>
            }
          />
        ) : (
          <>
            {/* ---------- 这一页怎么用（一句话，不解释实现） ---------- */}
            <Card className="bg-brand-50/60">
              <p className="text-sm text-slate-700">
                这些就是等一下要听写的词。<strong>点任意一个字</strong>，就能看它一笔一笔怎么写
                —— 先照着看一遍，再{playerHint}。
              </p>
            </Card>

            {/*
              ⚠️ 整批笔顺数据都没打进这次构建时（见 vite.config.ts 的 hanziStrokeData()），
                 这里**一次性说清**，而不是让孩子一个字一个字点、每次都收到同一句
                 "暂时没有笔顺数据" —— 那会让人以为是字的问题。
            */}
            {!strokesBundled && (
              <p className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                <span className="mt-0.5 shrink-0">
                  <IconAlertTriangle size={16} />
                </span>
                <span>
                  这次上线的版本里<strong>没有放进笔顺数据</strong>（构建时没找到数据包），
                  所以看不了笔顺动画。田字格和拼音都在，先照着字形写一遍；
                  修好之后这一块会自动出现。
                </span>
              </p>
            )}

            <Card>
              <CardHeader
                title="这次要听写的词"
                description={
                  strokesBundled
                    ? '每个字一个田字格，字上面是它的读音。点字看笔顺。'
                    : '每个字一个田字格，字上面是它的读音。'
                }
                actionAlign="start"
                action={
                  <div className="flex basis-full flex-wrap items-center gap-2">
                    <Link to={playerHref}>
                      <Button size="sm" icon={<IconVolume2 size={16} />}>
                        回去开始听写
                      </Button>
                    </Link>
                  </div>
                }
              />

              <ul className="space-y-3">
                {words.map((word) => (
                  <PreviewWordRow
                    key={word.questionId}
                    word={word}
                    selected={selected}
                    onSelect={setSelected}
                    audioState={audioState}
                    onPlayAudio={(target) => void playRecording(target)}
                    strokeOrderAvailable={strokesBundled}
                  />
                ))}
              </ul>
            </Card>
          </>
        )}

        {/*
          底部的相关页面链接（**不是**返回入口：返回入口只有左上角那个「←」，
          由 `backNav.ts` 按路由推导）。这些是"预习完了可能想去的地方"。
        */}
        <div className="flex flex-wrap gap-2">
          <Link to={playerHref}>
            <Button variant="secondary" size="sm">
              回上一页（做听写）
            </Button>
          </Link>
          {reviewHref !== undefined && (
            <Link to={reviewHref}>
              <Button variant="secondary" size="sm">
                看这次的全部题目
              </Button>
            </Link>
          )}
          <Link to={listHref}>
            <Button variant="secondary" size="sm">
              回听写页
            </Button>
          </Link>
        </div>
      </div>
    </AppShell>
  );
}

/** 一个词：词面 + 读音 + 错词/录音 + 这一行的田字格（点字看笔顺）。 */
function PreviewWordRow({
  word,
  selected,
  onSelect,
  audioState,
  onPlayAudio,
  strokeOrderAvailable,
}: {
  word: PreviewWord;
  selected: SelectedChar | null;
  onSelect: (next: SelectedChar | null) => void;
  audioState: { questionId: string; status: 'loading' | 'playing' } | null;
  onPlayAudio: (word: PreviewWord) => void;
  /** 这次构建有没有笔顺数据（没有就把格子做成纯展示，见面板上那条说明）。 */
  strokeOrderAvailable: boolean;
}) {
  const mistakeLabel = describePreviewMistake(word);
  const openChar =
    selected !== null && selected.questionId === word.questionId
      ? (word.chars.find((item) => item.position === selected.position) ?? null)
      : null;
  const audioBusy = audioState !== null && audioState.questionId === word.questionId;
  const audioPlaying = audioBusy && audioState?.status === 'playing';

  return (
    <li className="rounded-xl border border-slate-200 px-3 py-3">
      {/*
        ⚠️ 与批改页同一处修法：**词面不跟标签抢一行**。
        手机窄屏下"词面 flex-1 + 右侧一排 shrink-0 标签"会把词压成一条竖线
        （家长看到的就是"汉字没了"）。词面独占一行，标签换到下一行。
      */}
      <div className="flex min-w-0 items-baseline gap-2">
        <span className="shrink-0 text-xs text-slate-400">{word.position}</span>
        <span className="min-w-0 flex-1">
          <span className="block text-base font-semibold text-slate-900">{word.wordText}</span>
          <span className="block font-mono text-xs text-slate-500">
            {word.wordPinyin === '' ? '（这个词没有读音）' : word.wordPinyin}
          </span>
        </span>
      </div>

      <div className="mt-1 flex flex-wrap items-center gap-1.5">
        {/* 错词：`''` = 确实不在错词本里，那就不占位置（不显示"没错过"这种废话） */}
        {mistakeLabel !== '' && <Badge tone="warn">{mistakeLabel}</Badge>}

        {word.hasAudio === null ? (
          <Badge tone="warn">录音情况未获取</Badge>
        ) : word.hasAudio && word.wordId !== null ? (
          <Button
            size="sm"
            variant="secondary"
            loading={audioBusy && !audioPlaying}
            icon={<IconMic size={16} />}
            onClick={() => onPlayAudio(word)}
          >
            {audioPlaying ? '正在放…' : '听一遍录音'}
          </Button>
        ) : (
          <Badge tone="neutral">
            <span className="mr-1 inline-flex">
              <IconVolume2 size={12} />
            </span>
            语音朗读
          </Badge>
        )}
      </div>

      {/*
        整词拼音与字数对不上时**明说**，而不是把读音留空让人猜。
        读音不是从内置表补的（多音字的默认值经常就是错的），见 dictationPreview.ts 文件头。
      */}
      {word.wordPinyin !== '' && !word.pinyinAligned && (
        <p className="mt-2 flex items-start gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
          <span className="mt-0.5 shrink-0">
            <IconAlertTriangle size={12} />
          </span>
          <span>
            上面那行拼音的音节数和字数对不上，所以每个字头上没标读音。去字表里把它改成
            「一个字一个音节、用空格分开」就会显示出来。
          </span>
        </p>
      )}

      <div className="mt-2 flex flex-wrap gap-2">
        {word.chars.map((item) => {
          const isOpen = openChar !== null && openChar.position === item.position;
          return (
            <TianZiGeChar
              key={`${word.questionId}-${item.position}`}
              char={item.char}
              pinyin={item.pinyin}
              selected={isOpen}
              {...(strokeOrderAvailable
                ? {
                    onClick: () =>
                      onSelect(
                        isOpen ? null : { questionId: word.questionId, position: item.position },
                      ),
                  }
                : {})}
            />
          );
        })}
      </div>

      {openChar !== null && (
        <StrokeOrderCard
          char={openChar.char}
          pinyin={openChar.pinyin}
          onClose={() => onSelect(null)}
        />
      )}
    </li>
  );
}
